import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert, Stack } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DatabaseIcon from '@mui/icons-material/Storage';
import CombinedExportIcon from '@mui/icons-material/ImportExport';
import axios from '../../utils/axiosConfig';

import logger from '../../utils/logger';

interface ExportSporttechButtonProps {
  competitionId: string;
  competitionName?: string;
}

type ExportType = 'json' | 'db' | 'both';

const ExportSporttechButton: React.FC<ExportSporttechButtonProps> = ({ 
  competitionId, 
  competitionName 
}) => {
  const [loading, setLoading] = useState<ExportType | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const handleExport = async (exportType: ExportType) => {
    setLoading(exportType);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      switch (exportType) {
        case 'json':
          // Standard-Verhalten - nur JSON exportieren
          // Keine Parameter nötig - Standardverhalten
          break;
        case 'db':
          // Nur in DB speichern, kein JSON zurückgeben
          params.append('saveToDb', 'true');
          params.append('returnJson', 'false');
          break;
        case 'both':
          // In DB speichern und JSON zum Download zurückgeben
          params.append('saveToDb', 'true');
          // returnJson ist standardmäßig true, muss nicht gesetzt werden
          break;
      }
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`/api/admin/sporttechcomp/export/${competitionId}${queryString}`);
      
      if (exportType === 'json' || exportType === 'both') {
        // JSON-Datei zum Download erstellen wenn wir JSON zurückbekommen
        const exportData = response.data;
        
        // JSON-Datei zum Download erstellen
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        // Dateinamen mit Wettkampfnamen und Datum generieren
        const date = new Date().toISOString().split('T')[0];
        const safeName = exportData.competition.name
          .replace(/[^a-z0-9]/gi, '-')
          .toLowerCase()
          .replace(/-+/g, '-');
        const filename = `${safeName}-sporttechdata-${date}.json`;
        
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        
        // Aufräumen
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        
        setSuccessMessage(
          `${exportData.athleteCount} Athleten-Daten erfolgreich ${exportType === 'both' ? 'exportiert und in Datenbank gespeichert' : 'exportiert'}`
        );
      } else {
        // Bei DB-only Export die Nachricht vom Server verwenden
        setSuccessMessage(response.data.message || 'Sporttech-Startgruppen-Daten erfolgreich in Datenbank gespeichert');
      }
      
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Export. Bitte versuchen Sie es erneut.');
      //console.error('Export error:', err);
      logger.error('Export error:', err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <Stack spacing={1} direction="column" alignItems="flex-start">
        <Button
          variant="contained"
          color="primary"
          startIcon={loading === 'json' ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
          onClick={() => handleExport('json')}
          disabled={loading !== null}
          size="small"
          fullWidth
        >
          {loading === 'json' ? 'Exportiere...' : 'Sporttech-Startgruppen-Daten exportieren (JSON)'}
        </Button>
        
        <Button
          variant="contained"
          color="secondary"
          startIcon={loading === 'db' ? <CircularProgress size={20} color="inherit" /> : <DatabaseIcon />}
          onClick={() => handleExport('db')}
          disabled={loading !== null}
          size="small"
          fullWidth
        >
          {loading === 'db' ? 'Speichere in DB...' : 'Sporttech-Startgruppen-Daten in DB exportieren'}
        </Button>
        
        <Button
          variant="contained"
          color="info"
          startIcon={loading === 'both' ? <CircularProgress size={20} color="inherit" /> : <CombinedExportIcon />}
          onClick={() => handleExport('both')}
          disabled={loading !== null}
          size="small"
          fullWidth
        >
          {loading === 'both' ? 'Exportiere...' : 'Export JSON und DB'}
        </Button>
      </Stack>
      
      <Snackbar 
        open={success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportSporttechButton;